import { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { useAppSelector } from "@/app/hooks";
import { UNBUILT_PATHS } from "@/app/unbuilt-sections";
import { ConnectionProblem } from "@/components/ui/ConnectionProblem";
import { useGetCurrentAdminQuery } from "@/features/auth";
import { CriticalNoticeBanner, InboxMenu } from "@/features/notifications";
import {
  ANNOUNCEMENT_ACCESS,
  checkPermission,
  PERMISSIONS,
  type Permission,
} from "@/lib/permissions";
import { AccountMenu } from "@/components/layout/AccountMenu";
import { Button } from "@/components/ui/Button";
import { ThemeToggle } from "@/components/ui/ThemeToggle";
import { Wordmark } from "@/components/ui/Wordmark";
import {
  AdminsIcon,
  AnalyticsIcon,
  BalancingIcon,
  CharactersIcon,
  ChevronLeftIcon,
  CloseIcon,
  ContentIcon,
  DashboardIcon,
  EconomyIcon,
  LeaderboardsIcon,
  MatchesIcon,
  MatchmakingIcon,
  MenuIcon,
  MissionsIcon,
  NotificationsIcon,
  PlayersIcon,
  ProgressionIcon,
  RankingIcon,
  SecurityIcon,
  SocialIcon,
  StoreIcon,
  TournamentsIcon,
  UserIcon,
} from "@/components/ui/icons";
import { cn } from "@/lib/cn";
import { TINT_INK, type Tint } from "@/components/ui/tint";

type IconComponent = (props: React.SVGProps<SVGSVGElement>) => React.ReactElement;

interface NavItem {
  label: string;
  to: string;
  icon: IconComponent;
  /** Hidden entirely when the user lacks this (or every one of these). */
  permission: Permission | readonly Permission[];
  /** Shown to the account owner only, whatever rights a sub-admin holds. */
  ownerOnly?: boolean;
}

interface NavSection {
  title: string;
  /**
   * The section's colour — its heading dot and its icons at rest. Identity,
   * not state (see `tint.ts`): it is what lets the eye find "the green ones"
   * for economy without reading a label.
   */
  tint: Tint;
  items: NavItem[];
}

/**
 * Navigation, derived from permissions.
 *
 * Sections with nothing visible in them disappear rather than rendering an
 * empty heading — a `game_designer` should not see an "Economy" heading with no
 * links under it, because that reads as a broken page rather than as
 * intentional scoping.
 *
 * Every item carries an icon, and the icon is load-bearing rather than
 * decorative: it is the only thing left once the rail collapses, and it is what
 * makes a list of eighteen destinations scannable by shape instead of by
 * reading eighteen words.
 */
const NAV: NavSection[] = [
  {
    title: "Overview",
    tint: "brand",
    items: [
      {
        label: "Dashboard",
        to: "/",
        icon: DashboardIcon,
        permission: PERMISSIONS.DASHBOARD_VIEW,
      },
    ],
  },
  {
    title: "Players",
    tint: "info",
    items: [
      { label: "Players", to: "/players", icon: PlayersIcon, permission: PERMISSIONS.PLAYER_VIEW },
      { label: "Social", to: "/social", icon: SocialIcon, permission: PERMISSIONS.PLAYER_VIEW },
    ],
  },
  {
    title: "Game",
    tint: "accent",
    items: [
      {
        label: "Characters",
        to: "/characters",
        icon: CharactersIcon,
        permission: PERMISSIONS.CHARACTER_VIEW,
      },
      {
        label: "Balancing",
        to: "/balancing",
        icon: BalancingIcon,
        permission: PERMISSIONS.BALANCE_VIEW,
      },
      {
        label: "Missions",
        to: "/missions",
        icon: MissionsIcon,
        permission: PERMISSIONS.MISSION_VIEW,
      },
      {
        label: "Progression",
        to: "/progression",
        icon: ProgressionIcon,
        permission: PERMISSIONS.PROGRESSION_VIEW,
      },
    ],
  },
  {
    title: "Competitive",
    tint: "warning",
    items: [
      { label: "Matches", to: "/matches", icon: MatchesIcon, permission: PERMISSIONS.MATCH_VIEW },
      {
        label: "Matchmaking",
        to: "/matchmaking",
        icon: MatchmakingIcon,
        permission: PERMISSIONS.MATCHMAKING_VIEW,
      },
      {
        label: "Tournaments",
        to: "/tournaments",
        icon: TournamentsIcon,
        permission: PERMISSIONS.TOURNAMENT_VIEW,
      },
      { label: "Ranking", to: "/ranking", icon: RankingIcon, permission: PERMISSIONS.RANKING_VIEW },
      {
        label: "Leaderboards",
        to: "/leaderboards",
        icon: LeaderboardsIcon,
        permission: PERMISSIONS.LEADERBOARD_VIEW,
      },
    ],
  },
  {
    title: "Economy",
    tint: "success",
    items: [
      { label: "Economy", to: "/economy", icon: EconomyIcon, permission: PERMISSIONS.ECONOMY_VIEW },
      { label: "Store", to: "/store", icon: StoreIcon, permission: PERMISSIONS.STORE_VIEW },
    ],
  },
  {
    title: "Live ops",
    tint: "brand",
    items: [
      {
        label: "Announcements",
        to: "/announcements",
        icon: ContentIcon,
        permission: ANNOUNCEMENT_ACCESS,
      },
      {
        label: "Notifications",
        to: "/notifications",
        icon: NotificationsIcon,
        permission: PERMISSIONS.NOTIFICATION_VIEW,
      },
      {
        label: "Analytics",
        to: "/analytics",
        icon: AnalyticsIcon,
        permission: PERMISSIONS.ANALYTICS_VIEW,
      },
    ],
  },
  {
    title: "Administration",
    tint: "info",
    items: [
      {
        label: "Security",
        to: "/security",
        icon: SecurityIcon,
        permission: PERMISSIONS.SECURITY_VIEW,
      },
      {
        label: "Admins & roles",
        to: "/admins",
        icon: AdminsIcon,
        permission: PERMISSIONS.ADMIN_VIEW,
        ownerOnly: true,
      },
    ],
  },
];

/** Initials for the identity chip. Two letters is the most that stays legible at 32px. */
function monogram(name: string | null, email: string | null): string {
  const source = name?.trim() || email?.split("@")[0] || "?";
  const parts = source.split(/[\s._-]+/).filter(Boolean);
  const letters = parts.length >= 2 ? `${parts[0]![0]}${parts[1]![0]}` : source.slice(0, 2);
  return letters.toUpperCase();
}

/** Solid fills for the section headings' dots. */
const TINT_DOT: Record<Tint, string> = {
  brand: "bg-brand",
  success: "bg-success",
  accent: "bg-accent",
  warning: "bg-warning",
  danger: "bg-danger",
  info: "bg-info",
};

const RAIL_STORAGE_KEY = "aa.rail-collapsed";

/**
 * Role keys are machine identifiers; the top bar shows a person's job.
 *
 * Falls through to the raw key for a role created after this map was written,
 * which is wrong-looking but never blank — a custom role should not render an
 * empty line under someone's name.
 */
const ROLE_LABEL: Record<string, string> = {
  super_admin: "Super Admin",
  ops_lead: "Live Ops Lead",
  support: "Player Support",
  game_designer: "Game Designer",
  analyst: "Analyst",
  anticheat: "Anti-cheat",
};

/**
 * One nav row.
 *
 * The active state is three cues stacked, none of which is hue alone: a filled
 * wash, a weight change, and a lit bar pinned to the left edge. The bar is the
 * one that survives being glanced at from across a desk, and it is drawn with
 * `layoutId`-style persistence in mind — one element per row rather than a
 * single travelling indicator, because a travelling bar has to animate across
 * a scroll container and gets it wrong the moment the list is scrolled.
 */
function NavRow({ item, tint, collapsed }: { item: NavItem; tint: Tint; collapsed: boolean }) {
  const Glyph = item.icon;
  // Read from the same registry the router builds those routes from, so the
  // marker cannot say "Soon" about a section that has since gone live.
  const soon = UNBUILT_PATHS.has(item.to);

  // Not a link at all: there is no page behind it yet, and the route only
  // redirects. Disabled and out of the tab order, but still listed so the
  // rail shows what is coming.
  if (soon) {
    return (
      <div
        aria-disabled="true"
        title={collapsed ? `${item.label} (coming soon)` : "Coming soon"}
        className={cn(
          "rounded-control text-content-subtle relative flex items-center gap-3 py-2 text-sm select-none",
          collapsed ? "justify-center px-0" : "px-3",
        )}
      >
        <Glyph className={cn("size-4.5 shrink-0 opacity-50", TINT_INK[tint])} />
        {collapsed ? null : (
          <>
            <span className="truncate opacity-70">{item.label}</span>
            <span className="bg-warning-soft text-warning ml-auto shrink-0 rounded-full px-1.5 py-px text-[0.625rem] font-semibold tracking-wide uppercase">
              Soon
              <span className="sr-only"> (coming soon, not available)</span>
            </span>
          </>
        )}
      </div>
    );
  }

  return (
    <NavLink
      to={item.to}
      end={item.to === "/"}
      title={collapsed ? item.label : undefined}
      className={({ isActive }) =>
        cn(
          "group relative flex items-center gap-3 rounded-control py-2 text-sm",
          "transition-colors duration-150",
          collapsed ? "justify-center px-0" : "px-3",
          // Two cues, neither of them hue alone: a filled pill and a weight
          // change. Enough to survive being read at a glance from across a
          // desk without the row shouting at you the rest of the time.
          isActive
            ? "brand-gradient text-brand-content font-semibold shadow-md"
            : "text-content-muted hover:text-content hover:bg-surface-overlay",
        )
      }
    >
      {({ isActive }) => (
        <>
          <Glyph
            className={cn(
              "size-4.5 shrink-0 transition-opacity",
              isActive ? "text-brand-content" : TINT_INK[tint],
            )}
          />
          {/* Collapsed labels are removed from the tree, not hidden with
              opacity: a screen reader should not read out eighteen labels that
              are not on screen, and `title` already carries the name. */}
          {collapsed ? null : (
            <>
              <span className="truncate">{item.label}</span>
            </>
          )}
        </>
      )}
    </NavLink>
  );
}

function SidebarNav({ sections, collapsed }: { sections: NavSection[]; collapsed: boolean }) {
  return (
    <nav
      aria-label="Main"
      className="relative z-10 flex-1 overflow-x-hidden overflow-y-auto px-3 py-4"
    >
      {sections.map((section) => (
        <div key={section.title} className="mb-4 last:mb-0">
          {collapsed ? (
            // A rule instead of a heading. The grouping is still worth showing
            // when the labels are gone — it is what stops eighteen icons
            // reading as one undifferentiated column.
            <div aria-hidden="true" className="bg-border mx-auto mb-2 h-px w-5" />
          ) : (
            <p className="text-content-subtle flex items-center gap-2 px-3 pb-1.5 text-[0.6875rem] font-semibold tracking-wide uppercase">
              <span
                aria-hidden="true"
                className={cn("size-1.5 shrink-0 rounded-full", TINT_DOT[section.tint])}
              />
              {section.title}
            </p>
          )}
          <ul className="space-y-0.5">
            {section.items.map((item) => (
              <li key={item.to}>
                <NavRow item={item} tint={section.tint} collapsed={collapsed} />
              </li>
            ))}
          </ul>
        </div>
      ))}
    </nav>
  );
}

export function AppShell() {
  const session = useAppSelector((state) => state.session);
  const location = useLocation();

  /*
    The session load, shared with the guard above rather than repeated — both
    subscribe to one cache entry, so this is a read and a `refetch` handle, not
    a second request.

    `unavailable` means the token is fine and the server did not answer. The
    chrome stays up and the page area says so, because the alternative is
    rendering a nav with no permissions in it and a dashboard whose every query
    fails: an app that looks broken rather than one that is briefly offline.
  */
  const { refetch, isFetching } = useGetCurrentAdminQuery();
  const unavailable = session.status === "unavailable";
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Collapsed state is a per-browser convenience, so `localStorage` and a
  // try/catch — it can come back empty in a private window and the shell must
  // still render.
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(RAIL_STORAGE_KEY) === "1";
    } catch {
      return false;
    }
  });

  const toggleCollapsed = () => {
    setCollapsed((current) => {
      const next = !current;
      try {
        localStorage.setItem(RAIL_STORAGE_KEY, next ? "1" : "0");
      } catch {
        // Preference is lost on reload; the shell still works.
      }
      return next;
    });
  };

  const sections = NAV.map((section) => ({
    ...section,
    items: section.items.filter(
      (item) =>
        (!item.ownerOnly || session.is_owner) &&
        (typeof item.permission === "string" ? [item.permission] : item.permission).some(
          (permission) => checkPermission(session.permissions, permission),
        ),
    ),
  })).filter((section) => section.items.length > 0);

  // A drawer that survives navigation covers the page you just asked for.
  useEffect(() => setDrawerOpen(false), [location.pathname]);

  // Escape closes it. Expected of anything modal, and the only way out for
  // someone who cannot reach the backdrop with a pointer.
  useEffect(() => {
    if (!drawerOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setDrawerOpen(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [drawerOpen]);

  /**
   * The sidebar body, shared by the static rail and the drawer.
   *
   * `onClose` is what distinguishes them: the drawer needs an explicit dismiss
   * control, because the header's menu button sits *underneath* the open drawer
   * and the backdrop is not a discoverable affordance on a touch screen.
   */
  const sidebarBody = (isCollapsed: boolean, onClose?: () => void) => (
    <>
      <div
        className={cn(
          "flex h-16 shrink-0 items-center",
          isCollapsed ? "justify-center px-0" : "gap-2 px-4",
        )}
      >
        <Wordmark compact={isCollapsed} />
        {onClose ? (
          <Button
            variant="ghost"
            size="icon"
            className="ml-auto"
            aria-label="Close navigation"
            onClick={onClose}
          >
            <CloseIcon className="size-5" />
          </Button>
        ) : null}
      </div>

      <SidebarNav sections={sections} collapsed={isCollapsed} />

      {/* The arena artwork, as a quiet sign-off at the foot of the rail. Pure
          decoration — hidden from assistive tech and dropped when collapsed. */}

      {/* Profile sits below the nav proper, separated by a rule: it belongs to
          the signed-in admin rather than to the game, and grouping it with
          "Leaderboards" would put it in the scan path of someone looking for a
          game screen. */}
      <div className="border-border border-t p-3">
        <NavLink
          to="/settings/profile"
          title={isCollapsed ? "Profile" : undefined}
          className={({ isActive }) =>
            cn(
              "group flex items-center gap-3 rounded-control py-2 text-sm transition-colors duration-150",
              isCollapsed ? "justify-center px-0" : "px-3",
              isActive
                ? "brand-gradient text-brand-content font-semibold shadow-md"
                : "text-content-muted hover:text-content hover:bg-surface-overlay",
            )
          }
        >
          {({ isActive }) => (
            <>
              <UserIcon
                className={cn(
                  "size-4.5 shrink-0",
                  isActive ? "text-brand-content" : "text-content-subtle",
                )}
              />
              {isCollapsed ? null : <span>Profile</span>}
            </>
          )}
        </NavLink>

        {onClose ? null : (
          <button
            type="button"
            onClick={toggleCollapsed}
            aria-label={isCollapsed ? "Expand navigation" : "Collapse navigation"}
            className={cn(
              "text-content-subtle hover:text-content hover:bg-surface-sunken mt-0.5",
              "flex w-full items-center gap-3 rounded-control py-2 text-xs transition-colors",
              isCollapsed ? "justify-center px-0" : "px-3",
            )}
          >
            <ChevronLeftIcon
              className={cn(
                "size-4 shrink-0 transition-transform duration-300 ease-out-expo",
                isCollapsed && "rotate-180",
              )}
            />
            {isCollapsed ? null : <span>Collapse</span>}
          </button>
        )}
      </div>
    </>
  );

  return (
    <div className="bg-canvas relative flex h-full">
      {/* Static sidebar from `lg` up. Below that the panel is a drawer: at
          1024px a 256px rail costs a third of the width, which on a table of
          twelve columns is the difference between reading it and scrolling
          it. */}
      <aside
        className={cn(
          "app-rail border-border relative z-20 hidden shrink-0 flex-col border-r lg:flex",
          "transition-[width] duration-300 ease-out-expo",
          collapsed ? "w-16" : "w-62",
        )}
      >
        {sidebarBody(collapsed)}
      </aside>

      {/* Mobile drawer. Rendered unconditionally and translated off-screen so it
          animates in both directions — mounting on open can only ever animate
          the entrance. */}
      <div
        className={cn(
          "fixed inset-0 z-40 lg:hidden",
          drawerOpen ? "pointer-events-auto" : "pointer-events-none",
        )}
        aria-hidden={!drawerOpen}
      >
        <button
          type="button"
          tabIndex={drawerOpen ? 0 : -1}
          aria-label="Close navigation"
          onClick={() => setDrawerOpen(false)}
          className={cn(
            "absolute inset-0 bg-black/60 backdrop-blur-[2px] transition-opacity duration-200",
            drawerOpen ? "opacity-100" : "opacity-0",
          )}
        />
        <div
          className={cn(
            "app-rail border-border absolute inset-y-0 left-0 flex w-64 flex-col border-r shadow-lg",
            "transition-transform duration-300 ease-out-expo",
            drawerOpen ? "translate-x-0" : "-translate-x-full",
          )}
        >
          {sidebarBody(false, () => setDrawerOpen(false))}
        </div>
      </div>

      <div className="relative z-10 flex min-w-0 flex-1 flex-col">
        {/*
          Top bar.
          Holds the things that belong to the session rather than to the page:
          where you are, live status, the theme, who you are signed in as.
          Sticky, because "sign out" and "which admin am I?" are questions that
          come up halfway down a long list, not at the top of it.
        */}
        <header className="app-topbar bg-surface/85 sticky top-0 z-30 flex h-16 shrink-0 items-center gap-3 px-4 backdrop-blur-md sm:px-6">
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden"
            aria-label="Open navigation"
            aria-expanded={drawerOpen}
            onClick={() => setDrawerOpen(true)}
          >
            <MenuIcon className="size-5" />
          </Button>

          <div className="flex min-w-0 items-center lg:hidden">
            <Wordmark compact />
          </div>

          <div className="ml-auto flex shrink-0 items-center gap-1.5">
            <ThemeToggle />

            <InboxMenu />

            <div className="bg-border mx-1 hidden h-7 w-px sm:block" aria-hidden="true" />

            {/* Profile and sign-out live in here now. A standalone sign-out
                button beside the avatar is one stray click from ending
                someone's session mid-edit, and it took the space the one link
                people actually look for was missing from. */}
            <AccountMenu
              name={session.name ?? session.email ?? "Signed in"}
              email={session.email}
              role={ROLE_LABEL[session.role_keys[0] ?? ""] ?? session.role_keys[0] ?? "No role"}
              monogram={monogram(session.name, session.email)}
              avatarUrl={session.avatar_url}
            />
          </div>
        </header>

        {/* Raised by an unread `critical` notice; it stays until that is read. */}
        <CriticalNoticeBanner />

        <main className="app-canvas relative flex-1 overflow-y-auto">
          <div className="@container mx-auto px-4 py-6 sm:px-6 lg:px-8">
            {/* Keyed on the route so the entrance animation replays on every
                navigation. Without the key React reuses the subtree and the
                new page simply appears, which after a lazy-chunk fetch reads
                as a jump rather than as an arrival. */}
            <div key={unavailable ? "unavailable" : location.pathname} className="animate-rise">
              {unavailable ? (
                <ConnectionProblem onRetry={() => void refetch()} retrying={isFetching} />
              ) : (
                <Outlet />
              )}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
